import React, { useState, useRef, useEffect } from 'react';
import { OrbVisualization } from './components/OrbVisualization';
import { TimelineView } from './components/TimelineView';
import { DetailModal } from './components/DetailModal';
import { DataImport } from './components/DataImport';
import { dap27Data } from './data/dap27Data';
import { OrbData, FinancialData } from './types/FinancialData';
import './App.css';

function App() {
  const vizContainerRef = useRef<HTMLDivElement>(null);
  const [vizWidth, setVizWidth] = useState(900);

  useEffect(() => {
    const el = vizContainerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      const w = entries[0].contentRect.width;
      if (w > 0) setVizWidth(Math.floor(w));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const [financialData, setFinancialData] = useState<FinancialData>(() => {
    const saved = localStorage.getItem('financialData_v2');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return dap27Data;
      }
    }
    return dap27Data;
  });
  const [selectedOrb, setSelectedOrb] = useState<OrbData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showImport, setShowImport] = useState(false);
  
  const maxAmount = Math.max(...financialData.budgetItems.flatMap(item =>
    Object.values(item.yearlyAmounts).filter(amount => amount > 0)
  ));

  const [selectedYears, setSelectedYears] = useState<string[]>(['2026/2027']);

  const handleOrbClick = (orb: OrbData) => {
    setSelectedOrb(orb);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedOrb(null);
  };

  const handleDataImport = (newData: FinancialData) => {
    setFinancialData(newData);
    localStorage.setItem('financialData_v2', JSON.stringify(newData));
    setSelectedYears(newData.years.includes('2026/2027') ? ['2026/2027'] : [newData.years[0]]);
    setShowImport(false);
  };

  const handleResetToSample = () => {
    setFinancialData(dap27Data);
    localStorage.removeItem('financialData_v2');
    setSelectedYears(['2026/2027']);
  };

  const toggleYear = (year: string) => {
    setSelectedYears(prev =>
      prev.includes(year) ? prev.filter(y => y !== year) : [...prev, year]
    );
  };

  const toggleAllYears = () => {
    const forwardYears = financialData.years.slice(1);
    const allSelected = forwardYears.every(y => selectedYears.includes(y));
    setSelectedYears(allSelected ? ['2026/2027'] : forwardYears);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500">
      <nav className="bg-white/95 backdrop-blur-sm shadow-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center mr-3">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                  <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent leading-tight">
                  Budget Bubbles
                </h1>
                <p className="text-xs text-gray-500 leading-tight">Hutt City Council's Draft Annual Plan 2026/27</p>
              </div>
            </div>
            <div className="flex space-x-3">
              {/* Import functionality hidden for now */}
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="bg-white/90 backdrop-blur-sm rounded-xl shadow-xl border border-white/20 px-4 sm:px-6 py-4 sm:py-5 mb-4 sm:mb-6">
          <p className="text-base sm:text-lg font-semibold text-gray-800 mb-2">
            An interactive breakdown of Hutt City Council's capital spending — click any bubble to explore a project's full spending forecast.
          </p>
          <p className="text-sm text-gray-600 mb-3">
            Each bubble represents a capital project in the Draft Annual Plan 2026/27. Bubble size reflects the dollar value for the selected year(s). Use the year buttons to filter by financial year.
          </p>
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 leading-relaxed">
            <strong>Note:</strong> These figures show total project spend, not rates impact. Many projects receive external funding — transport projects typically receive 51% NZTA co-funding, others are fully covered by user fees, and some (like Silverstream Landfill) generate a surplus.
          </p>
        </div>

        {/* Import section hidden */}
        
        <div className="space-y-4">
          <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-xl border border-white/20 overflow-hidden">
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-6 py-4">
              <div className="flex items-center mb-3 text-white">
                <svg className="w-6 h-6 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z"/>
                  <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z"/>
                </svg>
                <h3 className="text-lg font-bold">Financial Overview — {
                  selectedYears.length === 0 || selectedYears.length === financialData.years.length
                    ? 'All Years'
                    : selectedYears.length === 1
                      ? selectedYears[0]
                      : selectedYears.map(y => y.split('/')[1]).join(' + ')
                }</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={toggleAllYears}
                  className={`px-3 py-1 text-xs font-bold rounded-lg transition-all border ${
                    financialData.years.slice(1).every(y => selectedYears.includes(y))
                      ? 'bg-white text-emerald-700 border-white'
                      : 'bg-transparent text-white border-white/60 hover:border-white hover:bg-white/20'
                  }`}
                >
                  All
                </button>
                {financialData.years.map(year => (
                  <button
                    key={year}
                    onClick={() => toggleYear(year)}
                    className={`px-3 py-1 text-xs font-bold rounded-lg transition-all border ${
                      selectedYears.includes(year)
                        ? 'bg-white text-emerald-700 border-white'
                        : 'bg-transparent text-white border-white/60 hover:border-white hover:bg-white/20'
                    }`}
                  >
                    {year === financialData.years[0]
                      ? `${year.split('/')[1]} (prior)`
                      : year.split('/')[1]}
                  </button>
                ))}
              </div>
            </div>
            <div className="p-0" ref={vizContainerRef}>
              <OrbVisualization
                data={financialData.budgetItems.filter(item => item.projectType !== 'opex')}
                categories={financialData.categories}
                selectedYears={selectedYears}
                onOrbClick={handleOrbClick}
                width={vizWidth}
                height={vizWidth < 500 ? Math.max(Math.round(vizWidth * 0.9), 380) : Math.max(Math.round(vizWidth * 0.75), 550)}
                maxAmount={maxAmount}
              />
            </div>
          </div>

          {/* Have Your Say button */}
          <a
            href="https://haveyoursay.huttcity.govt.nz/draft-annual-plan-2026-27"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => { window.goatcounter?.count({ path: 'have-your-say' }); }}
            className="flex items-center justify-center gap-3 w-full py-4 px-6 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white font-bold text-lg rounded-xl shadow-xl transition-all transform hover:scale-[1.02] active:scale-[0.98]"
          >
            <svg className="w-6 h-6 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd"/>
            </svg>
            Read more &amp; Have your say
          </a>
        </div>
      </div>

      <DetailModal
        orb={selectedOrb}
        budgetData={financialData.budgetItems}
        onClose={handleCloseModal}
        isOpen={isModalOpen}
      />
    </div>
  );
}

export default App;